import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useGetBorrowingRulesQuery } from "store/queries/benefitFallbacks";
import styled from "styled-components";

import {
  exceptionsInWords,
  neverBorrowsInWords,
  nonAnswerKindsInWords,
  pairLimitInWords,
  sourceLabel,
} from "./fallbacksWording";

const Heading = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1rem;
`;

const List = styled.ul`
  margin: 0 0 0.5rem;
  padding-left: 1.25rem;
  li {
    margin-bottom: 0.35rem;
  }
`;

/** A compact table in the page's own look: no fill, thin rules, the count right-aligned. */
const PairsTable = styled.table`
  border-collapse: collapse;
  margin: 0 0 0.75rem;
  th,
  td {
    text-align: left;
    vertical-align: top;
    padding: 0.4rem 1.5rem 0.4rem 0;
    border-bottom: 1px solid var(--surface-border, #dee2e6);
  }
  th {
    font-weight: 600;
  }
  .count {
    text-align: right;
    padding-right: 0;
  }
`;

const Paragraph = styled.p`
  margin: 0 0 0.5rem;
`;

export const BORROWING_INTRO =
  "When the payer gives no answer for the service line an estimate reads, the estimate falls back: it substitutes the co-insurance from a related service line, then uses earlier checks of the same plan by other patients, and otherwise leaves the value blank. These rules are by service line and are the same at every hospital.";

export const NEVER_BORROWED =
  "Copays, the deductible and the out-of-pocket are never substituted.";

export const SETTINGS_POINTER =
  "Each hospital's own settings are on its benefit selection screen: Hospitals › edit the hospital › Eligibility › New engine benefit selection configuration.";

/**
 * CLE-3047 — Eligibility › Fallbacks: the collapsible "How fallbacks work" section. One screen, in plain words,
 * for ops (Ani: "these rules are by service line not really by hospital"). It replaced the
 * CLE-3013 per-hospital table, which nobody had asked for (Mike, 2026-10-29).
 *
 * The pairs, the scopes they are limited to, the exceptions and the "only says to call" line
 * kinds all come from the API (`GET /eligibility/benefit-fallbacks`), built from the live map and
 * the engine's own constant, so the page stays true when the configuration changes. The
 * conditions around them are the engine's rules (`engine/ladder.ts`, `engine/engine.ts`) in words;
 * the MRI/CAT Scan and Diagnostic X-Ray sentences show only while a pair uses those lines.
 */
export function HowBorrowingWorks() {
  const { data, isLoading, isError } = useGetBorrowingRulesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  if (isLoading) {
    return (
      <div className="flex justify-content-center p-3">
        <ProgressSpinner
          style={{ width: "2rem", height: "2rem" }}
          data-testid="borrowing.loading"
        />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <Message
        severity="error"
        text="The fallback rules could not be loaded."
        data-testid="borrowing.error"
      />
    );
  }

  const { pairs, neverBorrows, nonAnswerLineKinds } = data;
  const mriBorrows = pairs.some((pair) => pair.reads === "MRI_CT");
  const xrayDonor = pairs.some((pair) => pair.borrowsFrom === "XRAY");
  const never = neverBorrowsInWords(neverBorrows);

  return (
    <Surface>
      <div data-testid="howBorrowingWorks">
        <Paragraph>{BORROWING_INTRO}</Paragraph>

        <Heading>Which co-insurance is substituted</Heading>
        {pairs.length === 0 ? (
          <Paragraph data-testid="borrowing.noPairs">
            No hospital substitutes co-insurance today.
          </Paragraph>
        ) : (
          <PairsTable data-testid="borrowing.pairs">
            <thead>
              <tr>
                <th>Service line</th>
                <th>Substitutes co-insurance from</th>
                <th className="count">Hospitals</th>
              </tr>
            </thead>
            <tbody>
              {pairs.map((pair) => {
                const limit = pairLimitInWords(pair);
                const exceptions = exceptionsInWords(pair);
                return (
                  <tr key={`${pair.reads}-${pair.borrowsFrom}`}>
                    <td>
                      {sourceLabel(pair.reads)}
                      {limit && (
                        <small className="block text-color-secondary">
                          {limit}
                        </small>
                      )}
                      {exceptions && (
                        <small
                          className="block text-color-secondary"
                          data-testid="borrowing.exceptions"
                        >
                          {exceptions}
                        </small>
                      )}
                    </td>
                    <td>{sourceLabel(pair.borrowsFrom)}</td>
                    <td className="count">{pair.accountCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </PairsTable>
        )}
        {never && <Paragraph data-testid="borrowing.never">{never}</Paragraph>}
        <Paragraph data-testid="borrowing.copays">{NEVER_BORROWED}</Paragraph>

        <Heading>When it is substituted</Heading>
        <Paragraph>
          Only when the service line&apos;s section in the payer&apos;s
          response:
        </Paragraph>
        <List data-testid="borrowing.conditions">
          <li>is missing entirely;</li>
          <li>came back with an error for that service type;</li>
          <li>
            only says to call: every line is{" "}
            {nonAnswerKindsInWords(nonAnswerLineKinds)}, and its summary has no
            copay, co-insurance or coverage answer. If the plan states one
            co-insurance for the whole plan, that is used instead;
          </li>
          {mriBorrows && (
            <li>
              for MRI/CAT Scan only: says just “Non-Covered”, with no copay or
              co-insurance on it.
            </li>
          )}
        </List>
        <Paragraph>
          If the section is there and simply lists no co-insurance, nothing is
          substituted: the estimate uses the plan&apos;s co-insurance where the
          plan states one, otherwise 0%. The substituted value is whatever the
          other service line reports, 0% included.
          {xrayDonor &&
            " From Diagnostic X-Ray only the facility co-insurance is used, never the radiologist's or office line."}
        </Paragraph>

        <Heading>If nothing can be substituted</Heading>
        <Paragraph data-testid="borrowing.history">
          The estimate uses earlier checks of the same payer, plan and group
          number by other patients only, never the same patient, read with the
          hospital&apos;s own ignore list and rules. All of those patients must
          agree; if none match or they disagree, the value is left blank. Copays
          skip substitution and go straight to these earlier checks, when their
          section is missing, came back with an error, or only says to call.
          {mriBorrows &&
            " An MRI/CAT Scan section that says just “Non-Covered” never uses earlier checks: if Diagnostic X-Ray has no co-insurance, the plan's co-insurance is used where the plan states one, otherwise 0%."}
        </Paragraph>

        <Paragraph
          className="text-color-secondary mb-0"
          data-testid="borrowing.settings"
        >
          {SETTINGS_POINTER}
        </Paragraph>
      </div>
    </Surface>
  );
}
